// ieee-applicationstate.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IEEEApplicationData } from '../../../../shared/models/ieee-application-data';

@Injectable({
  providedIn: 'root'
})
export class IEEEApplicationStateService {
  private readonly STORAGE_KEY = 'ieee_application_data';
  
  // Track dynamically if needed by the wizard components

  public showComputerSupplementPage = false;
  public showClaimSupplementPage = false;
  public emailTrackId?: number; 

  private masterDataSubject = new BehaviorSubject<IEEEApplicationData>({});
  public masterData$: Observable<IEEEApplicationData> = this.masterDataSubject.asObservable();

  constructor() {
    this.restoreFromStorage();
  }

  getLatestState(): IEEEApplicationData {
    return this.masterDataSubject.getValue();
  }

  getStepData<T = any>(stepKey: keyof IEEEApplicationData): T | undefined {
    return this.getLatestState()[stepKey] as T | undefined;
  }

  updateStepData(stepKey: keyof IEEEApplicationData, partialData: any): void {
    const freshState = {
      ...this.getLatestState(),
      [stepKey]: partialData
    };
    
    this.masterDataSubject.next(freshState);
    this.saveToStorage(freshState);
  }

  patchStepData(stepKey: keyof IEEEApplicationData, partialData: Record<string, any>): void {
    const currentState = this.getLatestState();
    const existingStepData = (currentState[stepKey] ?? {}) as Record<string, any>;

    const freshState = {
      ...currentState,
      [stepKey]: {
        ...existingStepData,
        ...partialData
      }
    };

    this.masterDataSubject.next(freshState);
    this.saveToStorage(freshState);
  }

 
getFinalSubmissionPayload(fileMetadata: any[], flatBinaries: File[]): any {
  const collectedData = this.getLatestState();

  // Combine binaries with their corresponding attachment code and custom name
  const packagedFiles = flatBinaries.map((file, index) => {
    const meta = fileMetadata[index] || {};
    return {
      file: file,                        // The actual raw File/Blob binary
      fileName: file.name,               // original file name
      attCode: meta.attCode || 'DEFAULT' // mapping the attachment code from metadata
    };
  });
  return {
    formData: {
      welcome: collectedData.welcome || null,
      coverage: collectedData.coverage || null,
      entityType: collectedData.entityType || null,   
      revenue: collectedData.revenue || null,
      practice: collectedData.practice || null,
      projectsContracts: collectedData.projectsContracts || null,
      priorInsurance: collectedData.priorInsurance || null,
      claimLincensing: collectedData.claimsLicensing || null,
      claimInfoSupplement: collectedData.claimInfoSupplement || null,
      riskPracticeQuestionsOne: collectedData.riskPracticeQuestionsOne || null,
      riskPracticeQuestionsTwo: collectedData.riskPracticeQuestionsTwo || null,
      reviewSign: collectedData.reviewSign || null,
      computerServicesSupplement: collectedData.computerServiceSupplement || null
      
    },
    files: packagedFiles 
  };
}

  clearApplicationSession(): void {
    const clearedState: IEEEApplicationData = {};
    this.showComputerSupplementPage = false;
    this.showClaimSupplementPage = false;
    this.emailTrackId = undefined;
    this.masterDataSubject.next(clearedState);
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(clearedState));
    } catch (error) {
      console.warn('Unable to clear session storage.', error);
    }
  }

  private saveToStorage(data: IEEEApplicationData): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Unable to persist form state.', error);
    }
  }

  private restoreFromStorage(): void {
    try {
      const savedProgress = sessionStorage.getItem(this.STORAGE_KEY);
      if (savedProgress) {
        this.masterDataSubject.next(JSON.parse(savedProgress));
      }
    } catch (error) {
      console.warn('Unable to restore form state.', error);
    }
  }
}